// Layer 3 (Transformations) for ProjectDashboardsScene — period selection and
// chart geometry for inline SVG. No React, no engine mutations.

import type {
  DashboardChartSeries,
  DashboardPeriod,
  DashboardPeriodData,
} from '@/versions/v1/shared/project-modules-seed';

export const DASHBOARD_PERIOD_LABELS: Record<DashboardPeriod, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
};

export type BarRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ChartGeometry = {
  bars: BarRect[];
  linePoints: string;
  width: number;
  height: number;
};

const CHART_WIDTH = 320;
const CHART_HEIGHT = 120;
const CHART_PAD = 8;

export function dashboardForPeriod(
  dashboards: Record<DashboardPeriod, DashboardPeriodData>,
  period: DashboardPeriod,
): DashboardPeriodData {
  return dashboards[period];
}

export type ChartGeometryOptions = {
  /** Scale bars from min * ratio instead of zero — exposes variation in tight ranges */
  minBaselineRatio?: number;
};

export function chartGeometry(
  series: DashboardChartSeries,
  options?: ChartGeometryOptions,
): ChartGeometry {
  const values = [...series.bar, ...series.line];
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values, 1);
  const min =
    options?.minBaselineRatio != null
      ? rawMin * options.minBaselineRatio
      : 0;
  const span = Math.max(rawMax - min, 1);
  const count = Math.max(series.bar.length, 1);
  const innerW = CHART_WIDTH - CHART_PAD * 2;
  const innerH = CHART_HEIGHT - CHART_PAD * 2;
  const step = innerW / count;

  const scaleY = (value: number) => CHART_PAD + innerH - ((value - min) / span) * innerH;

  const bars = series.bar.map((value, index) => {
    const w = step * 0.35;
    const x = CHART_PAD + index * step + (step - w) / 2;
    const top = scaleY(value);
    const bottom = scaleY(min);
    const h = Math.max(bottom - top, 0);
    return { x, y: top, width: w, height: h };
  });

  const linePoints = series.line
    .map((value, index) => {
      const x = CHART_PAD + index * step + step / 2;
      const y = scaleY(value);
      return `${x},${y}`;
    })
    .join(' ');

  return {
    bars,
    linePoints,
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
  };
}
